import React, { useEffect, useReducer, useState } from 'react';

import { health, TMapSettings } from '@constants';
import {
    GlobalIncidentsMap,
    StatisticsContainer,
    MainWrapper,
    mapReference,
    Header,
    Sidebar,
    TFilter,
    TSelectedCountry,
    Seo,
    ZoomControls,
    getFilterColumn,
} from '@components';
import {
    StyledMapContainer,
    TInitialState,
    TAction,
    THealthMapProperties,
} from '.';
import { THealthSecurityIncident } from '@types';
import {
    applyUrlFilters,
    calculateFilteredTotals,
    filterIncidents,
    apiToIncidentKeyMap,
    parseDate,
    updateUrlParameters,
    keyToLookupTable,
    lookupValueArray,
    lookupValue,
    incidentAPIDefaults,
    closePopups,
} from '@helpers';

export const INITIAL_STATE: TInitialState = {
    filter: {
        category: health.filters.categoryOptions[0].value,
        fromDate: health.filters.dates.from,
        toDate: health.filters.dates.to,
    },
    incidents: undefined,
    selectedCountry: undefined,
    globalStatistics: undefined,
};

function reducer(state: TInitialState, action: TAction) {
    switch (action.type) {
        case 'setIncidents':
            return { ...state, incidents: action.value };
        case 'updateFilters':
            return { ...state, filter: action.value };
        case 'setSelectedCountry':
            return { ...state, selectedCountry: action.value };
        case 'clearSelectedCountry':
            return { ...state, selectedCountry: undefined };
        case 'setFilteredStatistics':
            return { ...state, globalStatistics: action.value };
        default:
            return INITIAL_STATE;
    }
}

function mapAPIValueToGraphQL({
    key,
    value,
}: {
    key: string;
    value: string | number | number[];
}): string | string[] {
    if (value == undefined) return '';

    // Convert API numeric codes to human-readable strings using lookup tables
    const lookupTable = keyToLookupTable[key];
    if (lookupTable) {
        // eventCategories maps to array of strings
        if (Array.isArray(value)) {
            return lookupValueArray(value, lookupTable);
        }
        // Single fields (perpetrator, weapon, healthMeasure) map to one string
        return lookupValue(value, lookupTable);
    }

    return value.toString();
}

function mapAPIObjectToGraphQLNodes(jsonResponse): THealthSecurityIncident[] {
    return jsonResponse.map(object => {
        const mappedIncident = Object.fromEntries(
            Object.entries(object).map(([key, value]) => [
                apiToIncidentKeyMap[key] || key,
                mapAPIValueToGraphQL({ key, value }),
            ])
        );

        // Add missing columns from API in incident object
        return {
            ...incidentAPIDefaults,
            ...mappedIncident,
        };
    });
}

export function HealthMap({
    frontmatter,
    hideHeader,
    hideAbout,
    hideCategoriesFilters,
    hideAllCategoriesOption,
    selectedCategory,
    selectedArea,
    scrollZoomRequiresFocus,
    setGlobalStatistics,
}: THealthMapProperties) {
    const allCategoriesOption = health.filters.categoryOptions[0].value;

    const allVisibleCategories = hideAllCategoriesOption
        ? health.filters.categoryOptions.filter(
              option => option.value !== allCategoriesOption
          )
        : health.filters.categoryOptions;

    const defaultCategory = selectedCategory ?? allVisibleCategories[0].value;

    const initialState: TInitialState = {
        ...INITIAL_STATE,
        filter: {
            ...INITIAL_STATE.filter,
            category: defaultCategory,
        },
    };

    const [state, dispatch] = useReducer(reducer, initialState);
    // Delayed trigger for map navigation, decoupled from filter updates to avoid race conditions
    const [selectedAreaNavigation, setSelectedAreaNavigation] = useState<
        TMapSettings | undefined
    >();

    const [multipleCountries, setMultipleCountries] = useState(false);

    // Fetch data
    useEffect(() => {
        applyUrlFilters(initialState, dispatch);

        const fetchData = async () => {
            const response = await fetch(
                `https://sind-api.herokuapp.com/mapaction/v2/health`
            );
            const jsonResponse = await response.json();
            const convertedResponse = mapAPIObjectToGraphQLNodes(jsonResponse);

            dispatch({ type: 'setIncidents', value: convertedResponse });

            const conflictCategory = getFilterColumn('Conflict');
            const filteredIncidents = filterIncidents(
                convertedResponse,
                state.filter,
                conflictCategory
            );
            setGlobalStatistics?.(filteredIncidents);
        };

        fetchData().catch(console.error);
    }, []);

    function handleFilterChange(filter: TFilter): void {
        dispatch({ type: 'updateFilters', value: filter });
    }

    function handleSettingSelectedCountry(
        selectedCountry: TSelectedCountry
    ): void {
        dispatch({ type: 'setSelectedCountry', value: selectedCountry });
    }

    function handleClearingSelectedCountry(): void {
        dispatch({ type: 'setSelectedCountry', value: undefined });
        updateUrlParameters({ country: undefined });
        closePopups();
    }

    function handleSettingFilteredStatistics(
        filteredIncidents: THealthSecurityIncident[]
    ): void {
        const globalStatistics = calculateFilteredTotals(filteredIncidents);
        dispatch({ type: 'setFilteredStatistics', value: globalStatistics });
    }

    useEffect(() => {
        if (!selectedArea) {
            return;
        }
        const currentState: TFilter = state.filter;
        const newFilters: Partial<TFilter> = {};

        // Set category if it exists
        if (selectedArea.category) {
            newFilters.category = selectedArea.category;
        }
        // Set dates with fallback to initial state
        newFilters.fromDate = selectedArea.fromDate
            ? parseDate(selectedArea.fromDate)
            : INITIAL_STATE.filter.fromDate;

        newFilters.toDate = selectedArea.toDate
            ? parseDate(selectedArea.toDate)
            : INITIAL_STATE.filter.toDate;

        dispatch({
            type: 'updateFilters',
            value: {
                ...currentState,
                ...newFilters,
            },
        });
        // Trigger map navigation separately to avoid race condition with filter update
        setSelectedAreaNavigation(selectedArea);
    }, [selectedArea]);

    return (
        <StyledMapContainer>
            <Seo
                title={frontmatter.metadataPageTitle}
                description={frontmatter.metadataPageDescription}
            />
            <MainWrapper>
                {!hideHeader && (
                    <Header
                        title={frontmatter.healthMapHeading}
                        subtitle={frontmatter.healthMapSubheading}
                    />
                )}

                <Sidebar
                    filter={state.filter}
                    setFilter={handleFilterChange}
                    categoryOptions={allVisibleCategories}
                    hideAbout={hideAbout}
                    hideCategoriesFilters={hideCategoriesFilters}
                    hideHeader={hideHeader}
                />

                <GlobalIncidentsMap
                    filter={state.filter}
                    incidents={state.incidents}
                    selectedCountry={state.selectedCountry}
                    setSelectedCountry={handleSettingSelectedCountry}
                    clearSelectedCountry={handleClearingSelectedCountry}
                    setFilteredStatistics={handleSettingFilteredStatistics}
                    selectedAreaNavigation={selectedAreaNavigation}
                    scrollZoomRequiresFocus={scrollZoomRequiresFocus}
                    setMultipleCountries={setMultipleCountries}
                />

                {mapReference && (
                    <ZoomControls
                        map={mapReference.current}
                        hasSelectedCountry={Boolean(state.selectedCountry)}
                        hideHeader={hideHeader}
                    />
                )}

                {!multipleCountries && (
                    <StatisticsContainer
                        filter={state.filter}
                        filterCategory={health.filters.categoryOptions.find(
                            option => option.value === state.filter.category
                        )}
                        globalStatistics={state.globalStatistics}
                        selectedCountry={state.selectedCountry}
                        clearSelectedCountry={handleClearingSelectedCountry}
                        selectedArea={selectedArea}
                    />
                )}
            </MainWrapper>
        </StyledMapContainer>
    );
}
